import { mantineTheme } from 'init/mantine';
import { i18nKeys, type TranslateFunction } from 'locales';
import { isNonNullish, sumBy } from 'remeda';
import type { EscalationStage, PaidByReminder } from 'shared/hooks/use-load-paid-by-reminder';

const REMINDER_TYPES = i18nKeys.REMINDER_LIST.FILTERS.REMINDER_TYPES;
const LABELS = i18nKeys.ANALYTICS.PAID_BY_REMINDER;

const STAGE_LABELS: Record<EscalationStage, string> = {
  preventive_reminder: REMINDER_TYPES.PREVENTIVE_REMINDER,
  first_reminder: REMINDER_TYPES.FIRST_REMINDER,
  intermediate_reminder: REMINDER_TYPES.INTERMEDIATE_REMINDER,
  last_reminder: REMINDER_TYPES.LAST_REMINDER,
  initiate_ai_call: REMINDER_TYPES.INITIATE_AI_CALL,
  additional_reminder: REMINDER_TYPES.ADDITIONAL_REMINDER,
  formal_notice: REMINDER_TYPES.FORMAL_NOTICE,
  sent_to_third_party: LABELS.SENT_TO_THIRD_PARTY,
};

const STAGE_COLORS: Record<EscalationStage, string> = {
  preventive_reminder: mantineTheme.colors.purple[7],
  first_reminder: mantineTheme.colors.purple[6],
  intermediate_reminder: mantineTheme.colors.purple[5],
  last_reminder: mantineTheme.colors.purple[4],
  initiate_ai_call: mantineTheme.colors.purple[3],
  additional_reminder: mantineTheme.colors.warning[5],
  formal_notice: mantineTheme.colors.warning[7],
  sent_to_third_party: mantineTheme.colors.coral[9],
};

const STAGE_RANK: Record<EscalationStage, number> = {
  preventive_reminder: 0,
  first_reminder: 1,
  intermediate_reminder: 2,
  last_reminder: 3,
  initiate_ai_call: 4,
  additional_reminder: 5,
  formal_notice: 6,
  sent_to_third_party: 7,
};

const STILL_OPEN_COLOR = mantineTheme.colors.coral[7];

type SegmentTotals = {
  count: number;
  amount: number;
  share: number;
  label: string;
  color: string;
};

export type PaidByReminderSegment = SegmentTotals &
  ({ kind: 'step'; step: number; stepType: EscalationStage } | { kind: 'open' });

export const segmentKey = (segment: PaidByReminderSegment) =>
  segment.kind === 'open' ? 'open' : `step-${segment.step}`;

export function toStageLegendSegments(
  workflows: ReadonlyArray<Pick<PaidByReminder, 'steps' | 'open'>>,
  t: TranslateFunction,
): ReadonlyArray<PaidByReminderSegment> {
  const seen = new Map<string, PaidByReminderSegment>();

  for (const workflow of workflows) {
    for (const segment of toPaidByReminderSegments(workflow, t)) {
      // Keyed by stage, not by step: two workflows chase in a different order, so their
      // step 2 is rarely the same stage, and the legend speaks for all of them at once.
      if (!seen.has(stageKey(segment))) seen.set(stageKey(segment), segment);
    }
  }

  return [...seen.values()].sort(byEscalation);
}

const stageKey = (segment: PaidByReminderSegment) =>
  segment.kind === 'open' ? 'open' : segment.stepType;

const byEscalation = (one: PaidByReminderSegment, other: PaidByReminderSegment) =>
  rankOf(one) - rankOf(other);

const rankOf = (segment: PaidByReminderSegment) =>
  segment.kind === 'open' ? Number.POSITIVE_INFINITY : STAGE_RANK[segment.stepType];

export function toPaidByReminderSegments(
  { steps, open }: Pick<PaidByReminder, 'steps' | 'open'>,
  t: TranslateFunction,
): ReadonlyArray<PaidByReminderSegment> {
  const relanced = steps.flatMap(({ stepType, ...bucket }) =>
    isNonNullish(stepType) ? [{ ...bucket, stepType }] : [],
  );
  const relancedCount = sumBy(relanced, ({ count }) => count) + open.count;

  if (relancedCount === 0) return [];

  const shareOfRelanced = (count: number) => count / relancedCount;

  return [
    ...relanced.map(({ stepType, ...bucket }) => ({
      kind: 'step' as const,
      stepType,
      ...bucket,
      share: shareOfRelanced(bucket.count),
      label: t(STAGE_LABELS[stepType]),
      color: STAGE_COLORS[stepType],
    })),
    {
      kind: 'open' as const,
      ...open,
      share: shareOfRelanced(open.count),
      label: t(LABELS.STILL_OPEN),
      color: STILL_OPEN_COLOR,
    },
  ];
}
